// src/pages/auth/AdminLoginPage.tsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useTheme } from "../../config/theme.config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faUserShield,
  faEnvelope,
  faLock,
} from "@fortawesome/free-solid-svg-icons";

import styles from "../../styles/auth/AdminLoginPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";

const AdminLoginPage: React.FC = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { loginAdmin } = useAdminAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await loginAdmin(formData.email, formData.password);
      if (response) {
        navigate("/admin/dashboard", { replace: true });
      } else {
        setError("Acceso denegado: Credenciales inválidas");
      }
    } catch (error) {
      setError("Error crítico del sistema de autenticación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authPage}>
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <div className={styles.authCardContent}>
            <div className={styles.authHeader}>
              <div className={styles.authBrand}>
                <h1 className={styles.authBrandTitle}>ADMINISTRACIÓN</h1>
              </div>
              <h2 className={styles.authTitle}>Control de Acceso</h2>
              <p className={styles.authSubtitle}>
                Identifíquese para acceder al gestor de {theme.brand.name}
              </p>
            </div>

            {error && <div className={`${styles.authAlert} ${styles.error}`}>{error}</div>}

            <form onSubmit={handleSubmit} noValidate>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.formLabel}>
                  Cuenta de Usuario
                </label>
                <div className={styles.inputContainer}>
                  <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={styles.authFormInput}
                    placeholder="usuario@admin.com"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.formLabel}>
                  Clave de Seguridad
                </label>
                <div className={styles.inputContainer}>
                  <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className={styles.authFormInput}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={styles.inputButton}
                    title={showPassword ? "Ocultar clave" : "Mostrar clave"}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className={`${btnStyles.primary} ${btnStyles.fullWidth}`} style={{ marginTop: '16px' }}>
                {loading ? (
                  <div className={styles.loadingSpinner}></div>
                ) : (
                  <>
                    Autenticar Acceso
                  </>
                )}
              </button>
            </form>

            <div className={styles.authFooter}>
              <Link to="/" className={styles.backLink}>
                ← Volver al portal público
              </Link>
              <div className={styles.securityNote}>
                <FontAwesomeIcon icon={faLock} size="xs" />
                Conexión segura cifrada
              </div>
            </div>
          </div>
        </div>

        <div className={styles.authSideMessage}>
          <div className={styles.sideMessageContent}>
            <div className={styles.messageBadge}>
              <FontAwesomeIcon icon={faUserShield} /> Gestión Interna
            </div>
            <h2>Panel de Administrador</h2>
            <p>
              Acceso exclusivo para personal autorizado. Controle envíos, usuarios y configuraciones globales desde este centro operativo.
            </p>
            <div style={{ marginTop: '20px', opacity: 0.8, fontSize: '13px' }}>
              <FontAwesomeIcon icon={faLock} style={{ marginRight: '10px' }} />
              Sesión protegida por cifrado avanzado.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
